import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, TextField } from '../components/ui.jsx';
import { ListIcon, MapPin } from '../components/icons.jsx';
import { lookupPostcode, ApiError } from '../lib/api.js';

/**
 * Step one: the postcode. This is the highest-value field on the whole journey
 * — everything downstream depends on it — so it gets a forgiving input
 * (spacing and case are normalised for the customer) and the clearest error
 * copy.
 */
export default function PostcodeStep({
  journey,
  initialPostcode,
  onFound,
  autoFocus = false,
  autoSubmit = false,
}) {
  const [postcode, setPostcode] = useState(initialPostcode ?? '');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const runLookup = useCallback(
    async (raw) => {
      const trimmed = raw.trim();

      if (!trimmed) {
        setError('Please enter your postcode');
        inputRef.current?.focus();
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);

      try {
        const result = await lookupPostcode(trimmed, controller.signal);

        if (!result.addresses.length) {
          setError(
            'We could not find any addresses for that postcode. Please check it and try again.',
          );
          return;
        }

        onFound({ postcode: result.postcode, addresses: result.addresses });
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(
          err instanceof ApiError
            ? err.fieldErrors.postcode || err.message
            : 'Something went wrong. Please try again.',
        );
      } finally {
        setLoading(false);
      }
    },
    [onFound],
  );

  useEffect(() => {
    // Only when the customer has come *back* to this step. Grabbing focus on
    // the initial page view would skip past the heading for keyboard and
    // screen-reader users, and open the keyboard on mobile before they have
    // read anything.
    if (autoFocus) inputRef.current?.focus();
    return () => abortRef.current?.abort();
  }, [autoFocus]);

  // A postcode typed into the homepage hero has already been "submitted" as far
  // as the customer is concerned, so it runs straight away rather than making
  // them press a second button on a page they never asked to see. A failure
  // surfaces here as an ordinary error on a filled-in field.
  const autoRan = useRef(false);
  useEffect(() => {
    if (!autoSubmit || autoRan.current || !initialPostcode) return;
    autoRan.current = true;
    runLookup(initialPostcode);
  }, [autoSubmit, initialPostcode, runLookup]);

  const handleSubmit = (event) => {
    event.preventDefault();
    runLookup(postcode);
  };

  return (
    <Card>
      <form onSubmit={handleSubmit} noValidate>
        {error && <Alert>{error}</Alert>}

        <TextField
          ref={inputRef}
          label="Enter Your Postcode"
          icon={ListIcon}
          trailingIcon={MapPin}
          name="postcode"
          placeholder="Type here..."
          value={postcode}
          onChange={(e) => {
            setPostcode(e.target.value.toUpperCase());
            if (error) setError(null);
          }}
          error={error ? ' ' : undefined}
          autoComplete="postal-code"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck="false"
          inputMode="text"
          maxLength={8}
          enterKeyHint="search"
          required
        />

        <Button type="submit" loading={loading}>
          {journey?.config?.ctaLabel ?? 'Next Step'}
        </Button>
      </form>
    </Card>
  );
}
